import { conditionalRender } from '@repo/toolkit/conditional-render'
import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { getPeriods } from '../../../api/periods/get-periods'
import { getTripAddresses } from '../../../api/trip-addresses/get-trip-addresses'
import {
  MissionStatusEnum,
  missionStatusEnumMapping
} from '../../../lib/types/enums/mission/mission-status.enum'
import {
  MissionTypeEnum,
  missionTypeEnumMapping
} from '../../../lib/types/enums/mission/mission-type.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { MissionFilter } from '../../../lib/types/models/mission/mission-filter'

interface Props {
  filter?: MissionFilter
  onFilterChange: (filter: MissionFilter) => void
}

export function MissionsFilter({ filter, onFilterChange }: Props) {
  const { t } = useTranslation()

  function setFilter<K extends keyof MissionFilter>(key: K, value: MissionFilter[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: tripAddresses } = useQuery({
    queryKey: ['trip-addresses'],
    queryFn: () => getTripAddresses()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  return (
    <div className={'flex flex-col gap-4 mt-2'}>
      <MultiSelectInput
        searchable
        label={'Périodes'}
        variant={'warning'}
        options={periods?.data?.map((period) => ({ label: period.name, value: period.name }))}
        onChange={(periods) => setFilter('periods', periods)}
        selectedOptions={filter?.periods?.map((periodId) => ({
          label: periods?.data?.find((p) => p.name === periodId)?.name || '',
          value: periodId
        }))}
      />
      <DateStringInput
        label={'Date du la mission'}
        value={filter?.missionDate}
        onChange={(date) => setFilter('missionDate', date)}
      />
      <SelectInput
        label={'Statut'}
        options={Object.values(MissionStatusEnum).map((type) => ({
          label: missionStatusEnumMapping[type].label,
          value: type
        }))}
        onChange={(type) => setFilter('status', type)}
        selectedOption={conditionalRender(
          !!filter?.status && !!missionStatusEnumMapping[filter.status],
          {
            label: missionStatusEnumMapping[filter!.status!]?.label || '',
            value: filter!.status!
          }
        )}
      />
      <SelectInput
        label={'Type'}
        options={Object.values(MissionTypeEnum).map((type) => ({
          label: missionTypeEnumMapping[type].label,
          value: type
        }))}
        onChange={(type) => setFilter('type', type)}
        selectedOption={
          filter?.type && missionTypeEnumMapping[filter.type]
            ? { label: missionTypeEnumMapping[filter.type].label, value: filter.type }
            : undefined
        }
      />
      <MultiSelectInput
        label={'Lieu de RDV'}
        variant={'primary'}
        searchable
        options={tripAddresses?.data.map((tripAddress) => ({
          label: tripAddress.label,
          value: tripAddress.id
        }))}
        onChange={(selectedOptions) => setFilter('meetingPlaceIds', selectedOptions)}
        selectedOptions={filter?.meetingPlaceIds?.map((id) => ({
          label: tripAddresses?.data.find((tripAddress) => tripAddress.id === id)?.label ?? '',
          value: id
        }))}
      />
      <SelectInput
        label={'Lieux de départ'}
        searchable
        options={tripAddresses?.data.map((tripAddress) => ({
          label: tripAddress.label,
          value: tripAddress.id
        }))}
        onChange={(selectedOptions) => setFilter('departurePlaceId', selectedOptions)}
        selectedOption={conditionalRender(!!filter?.departurePlaceId, {
          label:
            tripAddresses?.data.find((tripAddress) => tripAddress.id === filter?.departurePlaceId)
              ?.label ?? '',
          value: filter?.departurePlaceId
        })}
      />
      <SelectInput
        label={"Lieux d'arrivée"}
        searchable
        options={tripAddresses?.data.map((tripAddress) => ({
          label: tripAddress.label,
          value: tripAddress.id
        }))}
        onChange={(selectedOptions) => setFilter('arrivalPlaceId', selectedOptions)}
        selectedOption={conditionalRender(!!filter?.arrivalPlaceId, {
          label:
            tripAddresses?.data.find((tripAddress) => tripAddress.id === filter?.arrivalPlaceId)
              ?.label ?? '',
          value: filter?.arrivalPlaceId
        })}
      />
      <MultiSelectInput
        variant="primary"
        label={'Qualifications requises'}
        onChange={(type) => setFilter('qualifications', type)}
        options={Object.values(StaffQualificationEnum).map((type) => ({
          label: t(staffQualificationEnumMapping[type].label),
          value: type
        }))}
        selectedOptions={
          filter?.qualifications?.map((type) => ({
            label: t(staffQualificationEnumMapping[type].label),
            value: type
          })) ?? []
        }
      />
    </div>
  )
}
